import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Rows3, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import CultivarPicker, { type PickedCultivar } from './CultivarPicker';
import orchardService from '../../services/orchard';
import { parseSequences } from '../../utils/nurseryRowSlots';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { cn } from '../../utils/cn';
import type { CultivarArea, OrchardPosition, OrchardPositionPlan } from '../../types/orchard';

/** The server's limit on one add: rows × positions. */
const MAX_POSITIONS = 5000;

/** One colour per cultivar, in the order the summary lists them. */
const CHIP_COLOURS = [
  'bg-emerald-50 text-emerald-800 border-emerald-200',
  'bg-sky-50 text-sky-800 border-sky-200',
  'bg-amber-50 text-amber-800 border-amber-200',
  'bg-violet-50 text-violet-800 border-violet-200',
  'bg-rose-50 text-rose-800 border-rose-200',
  'bg-teal-50 text-teal-800 border-teal-200',
  'bg-orange-50 text-orange-800 border-orange-200',
  'bg-indigo-50 text-indigo-800 border-indigo-200',
];

const nameOf = (name: string | null | undefined, trade: string | null | undefined) =>
  trade ? `${name} (${trade})` : (name ?? 'Unknown cultivar');

const trees = (n: number) => `${n.toLocaleString('en-AU')} ${n === 1 ? 'tree' : 'trees'}`;

const inputClass = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';
const labelClass = 'block text-sm font-medium text-neutral-700 mb-1';

const errorText = (e: unknown, fallback: string) =>
  (e as { message?: string })?.message || fallback;

interface RowPositionPlannerProps {
  area: CultivarArea;
  /** The area's tree count follows its positions; the page shows the new figure. */
  onTreeCountChange?: (treeCount: number) => void;
}

/**
 * Rows and positions on a farm that plans by position, the way nursery blocks
 * are laid out: a run of rows, positions along them (10,20,30 or 10-60), and
 * the cultivar and trees in each stretch. The area's tree count is the total.
 */
function RowPositionPlanner({ area, onTreeCountChange }: RowPositionPlannerProps) {
  const orgTz = useOrgTimezone();
  const [plan, setPlan] = useState<OrchardPositionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // The add form.
  const [startRow, setStartRow] = useState<number | ''>('');
  const [endRow, setEndRow] = useState<number | ''>('');
  const [seqStr, setSeqStr] = useState('10');
  const [cultivar, setCultivar] = useState<PickedCultivar | null>(null);
  const [treeCount, setTreeCount] = useState<number | ''>('');
  const [rootstock, setRootstock] = useState<PickedCultivar | null>(null);
  const [plantingDate, setPlantingDate] = useState('');
  const [notes, setNotes] = useState('');
  const [replace, setReplace] = useState(false);
  const [adding, setAdding] = useState(false);

  // Selecting and editing.
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState<OrchardPosition | null>(null);

  const apply = useCallback((next: OrchardPositionPlan) => {
    setPlan(next);
    if (next.row_count > 0) onTreeCountChange?.(next.total_trees);
  }, [onTreeCountChange]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await orchardService.getPositions(area.id);
        if (live && res.success && res.data) setPlan(res.data);
        else if (live) setError('Could not load the rows.');
      } catch (e) {
        if (live) setError(errorText(e, 'Could not load the rows.'));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [area.id]);

  const colourFor = useMemo(() => {
    const order = new Map((plan?.summary ?? []).map((c, i) => [c.cultivar_id, i]));
    return (cultivarId: number) => CHIP_COLOURS[(order.get(cultivarId) ?? 0) % CHIP_COLOURS.length];
  }, [plan]);

  const sequences = useMemo(() => parseSequences(seqStr), [seqStr]);
  const lastRow = endRow === '' ? startRow : endRow;
  const rowsBackwards = startRow !== '' && lastRow !== '' && lastRow < startRow;
  const rowCount = startRow === '' || lastRow === '' || rowsBackwards ? 0 : Number(lastRow) - Number(startRow) + 1;
  const positionCount = rowCount * sequences.length;
  const tooMany = positionCount > MAX_POSITIONS;
  const canAdd = positionCount > 0 && !tooMany && !!cultivar && treeCount !== '' && treeCount > 0;

  const rows = plan?.rows ?? [];
  const selectedPositionIds = rows
    .filter((r) => selectedRows.has(r.row_number))
    .flatMap((r) => r.positions.map((p) => p.id));

  const addPositions = async () => {
    if (!canAdd || !cultivar) return;
    setAdding(true);
    setError(null);
    setNote(null);
    try {
      const res = await orchardService.addPositions(area.id, {
        start_row: Number(startRow),
        end_row: Number(lastRow),
        sequences,
        cultivar_id: cultivar.id,
        tree_count: Number(treeCount),
        rootstock_cultivar_id: rootstock?.id ?? null,
        planting_date: plantingDate || null,
        notes: notes.trim() || null,
        replace,
      });
      if (res.success && res.data) {
        apply(res.data);
        const { added = 0, replaced = 0, skipped = 0 } = res.data;
        const parts = [`${added} ${added === 1 ? 'position' : 'positions'} added`];
        if (replaced) parts.push(`${replaced} replaced`);
        if (skipped) parts.push(`${skipped} already planted and kept (tick "Replace" to change them)`);
        setNote(`${parts.join(', ')}.`);
        // Ready for the next run: the rows after this one.
        setStartRow(Number(lastRow) + 1);
        setEndRow('');
      } else {
        setError('Could not add the rows.');
      }
    } catch (e) {
      setError(errorText(e, 'Could not add the rows.'));
    } finally {
      setAdding(false);
    }
  };

  const deleteSelected = async () => {
    if (selectedPositionIds.length === 0) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await orchardService.deletePositions(area.id, selectedPositionIds);
      if (res.success && res.data) {
        apply(res.data);
        setSelectedRows(new Set());
        setConfirmingDelete(false);
        setEditing(null);
      } else {
        setError('Could not delete the rows.');
      }
    } catch (e) {
      setError(errorText(e, 'Could not delete the rows.'));
    } finally {
      setDeleting(false);
    }
  };

  const toggleRow = (row: number) => {
    setConfirmingDelete(false);
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(row)) next.delete(row); else next.add(row);
      return next;
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-5 flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading rows…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-5 space-y-5">
        <div className="flex items-start gap-2">
          <Rows3 className="h-4 w-4 text-neutral-500 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-neutral-700">Rows and positions</h3>
            <p className="text-xs text-neutral-500 break-words">
              What stands in each stretch of each row. The area&apos;s tree count is the total of its positions.
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-800 break-words">{error}</div>
        )}

        {/* What is planted, per cultivar */}
        {plan && plan.row_count > 0 && (
          <div>
            <p className="text-sm text-neutral-700">
              <span className="font-semibold">{trees(plan.total_trees)}</span> in {plan.row_count}{' '}
              {plan.row_count === 1 ? 'row' : 'rows'}, {plan.summary.length}{' '}
              {plan.summary.length === 1 ? 'cultivar' : 'cultivars'}
            </p>
            <ul className="mt-2 flex flex-wrap gap-2">
              {plan.summary.map((c) => (
                <li key={c.cultivar_id}
                  className={cn('px-2.5 py-1 rounded-full text-xs border max-w-full break-words', colourFor(c.cultivar_id))}>
                  <span className="font-semibold">{nameOf(c.cultivar_name, c.cultivar_trade_name)}</span>
                  {' · '}{trees(c.trees)} · {c.rows} {c.rows === 1 ? 'row' : 'rows'}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Add a run of rows */}
        <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-semibold text-neutral-800">Plant rows</h4>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <label htmlFor="pos-start-row" className={labelClass}>From row</label>
              <input id="pos-start-row" type="number" min="1" value={startRow}
                onChange={(e) => setStartRow(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                className={inputClass} />
            </div>
            <div>
              <label htmlFor="pos-end-row" className={labelClass}>To row</label>
              <input id="pos-end-row" type="number" min="1" value={endRow}
                placeholder={startRow === '' ? '' : String(startRow)}
                onChange={(e) => setEndRow(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                className={inputClass} />
            </div>
            <div className="col-span-2">
              <label htmlFor="pos-sequences" className={labelClass}>Positions in each row</label>
              <input id="pos-sequences" value={seqStr} onChange={(e) => setSeqStr(e.target.value)}
                placeholder="10,20,30 or 10-60" className={inputClass} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <CultivarPicker
              label="Cultivar"
              placeholder="Search cultivars..."
              cultivarRole="scion"
              value={cultivar?.id ?? null}
              onChange={setCultivar}
            />
            <div>
              <label htmlFor="pos-trees" className={labelClass}>Trees in each position</label>
              <input id="pos-trees" type="number" min="1" value={treeCount}
                onChange={(e) => setTreeCount(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                className={inputClass} />
            </div>
            <CultivarPicker
              label="Rootstock (optional)"
              hint="Left blank, the area's rootstock."
              placeholder="Search rootstock cultivars..."
              cultivarRole="rootstock"
              value={rootstock?.id ?? null}
              onChange={setRootstock}
            />
            <div>
              <label htmlFor="pos-planted" className={labelClass}>Planted (optional)</label>
              <input id="pos-planted" type="date" value={plantingDate}
                onChange={(e) => setPlantingDate(e.target.value)} className={inputClass} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="pos-notes" className={labelClass}>Notes (optional)</label>
              <input id="pos-notes" value={notes} maxLength={1000}
                onChange={(e) => setNotes(e.target.value)} className={inputClass} />
            </div>
          </div>

          <label className="inline-flex items-start gap-2 text-sm text-neutral-700">
            <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-neutral-300"
              checked={replace} onChange={(e) => setReplace(e.target.checked)} />
            <span>Replace positions already planted</span>
          </label>

          <div className="flex items-center gap-3 flex-wrap">
            <Button variant="primary" size="sm" onClick={addPositions} disabled={adding || !canAdd}>
              <Plus className="h-4 w-4" />
              {adding ? 'Adding…' : positionCount > 1 ? `Add ${positionCount} positions` : 'Add position'}
            </Button>
            {rowsBackwards ? (
              <p className="text-sm text-red-700">The last row comes before the first.</p>
            ) : tooMany ? (
              <p className="text-sm text-red-700 break-words">
                That is {positionCount.toLocaleString('en-AU')} positions; add at most {MAX_POSITIONS.toLocaleString('en-AU')} at a time.
              </p>
            ) : positionCount > 0 ? (
              <p className="text-sm text-neutral-600 min-w-0 break-words">
                {rowCount === 1 ? `Row ${startRow}` : `Rows ${startRow} to ${lastRow}`}, positions{' '}
                {sequences.length > 8
                  ? `${sequences.slice(0, 3).join(', ')} … ${sequences[sequences.length - 1]} (${sequences.length})`
                  : sequences.join(', ')}
                {treeCount !== '' && treeCount > 0 ? `: ${trees(positionCount * Number(treeCount))}` : ''}.
              </p>
            ) : (
              <p className="text-xs text-neutral-400 break-words">
                Positions are numbered in tens so one can be slotted in between later. A range between two tens
                (10-60) is every tenth.
              </p>
            )}
          </div>
          {note && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-800 break-words">{note}</div>
          )}
        </div>

        {/* The rows */}
        {rows.length === 0 ? (
          <p className="text-sm text-neutral-400">No rows planted yet.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap border-b border-neutral-100 pb-3">
              <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
                <input type="checkbox" className="h-4 w-4 rounded border-neutral-300"
                  checked={selectedRows.size === rows.length}
                  onChange={(e) => {
                    setConfirmingDelete(false);
                    setSelectedRows(e.target.checked ? new Set(rows.map((r) => r.row_number)) : new Set());
                  }} />
                Select all rows
              </label>
              {selectedRows.size > 0 && !confirmingDelete && (
                <Button variant="danger" size="sm" onClick={() => { setEditing(null); setConfirmingDelete(true); }}>
                  <Trash2 className="h-4 w-4" /> Delete {selectedRows.size} {selectedRows.size === 1 ? 'row' : 'rows'}
                </Button>
              )}
            </div>
            <p className="text-xs text-neutral-400">Tap a position to change it, move it or take it out.</p>

            {confirmingDelete && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-3 text-sm text-red-800">
                <p className="break-words">
                  Delete {selectedRows.size} {selectedRows.size === 1 ? 'row' : 'rows'} and the{' '}
                  {selectedPositionIds.length} {selectedPositionIds.length === 1 ? 'position' : 'positions'} in{' '}
                  {selectedRows.size === 1 ? 'it' : 'them'}?
                </p>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <Button variant="danger" size="sm" onClick={deleteSelected} disabled={deleting}>
                    {deleting ? 'Deleting…' : 'Delete'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmingDelete(false)} disabled={deleting}>Cancel</Button>
                </div>
              </div>
            )}

            {rows.map((r) => (
              <div key={r.row_number}>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-700 w-36 flex-shrink-0">
                    <input type="checkbox" className="h-4 w-4 rounded border-neutral-300"
                      checked={selectedRows.has(r.row_number)} onChange={() => toggleRow(r.row_number)} />
                    Row {r.row_number}
                    <span className="font-normal text-xs text-neutral-500">{r.trees.toLocaleString('en-AU')}</span>
                  </label>
                  {r.positions.map((p) => (
                    <button key={p.id} type="button"
                      onClick={() => { setConfirmingDelete(false); setEditing(editing?.id === p.id ? null : p); }}
                      aria-label={`Row ${r.row_number} position ${p.sequence_number}: ${nameOf(p.cultivar_name, p.cultivar_trade_name)}, ${trees(p.tree_count)}`}
                      className={cn(
                        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border max-w-full text-left hover:underline',
                        colourFor(p.cultivar_id),
                        editing?.id === p.id && 'ring-2 ring-primary-400',
                      )}>
                      <span className="font-mono">S{p.sequence_number}</span>
                      <span className="break-words min-w-0">{p.cultivar_name}</span>
                      <span className="font-semibold">×{p.tree_count}</span>
                    </button>
                  ))}
                </div>
                {editing && r.positions.some((p) => p.id === editing.id) && (
                  <PositionEditor
                    key={editing.id}
                    position={editing}
                    plantedLabel={editing.planting_date ? formatLocalDateInTz(editing.planting_date, orgTz) : null}
                    onSaved={(next) => { apply(next); setEditing(null); }}
                    onCancel={() => setEditing(null)}
                    onError={setError}
                    areaId={area.id}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface PositionEditorProps {
  areaId: number;
  position: OrchardPosition;
  plantedLabel: string | null;
  onSaved: (plan: OrchardPositionPlan) => void;
  onCancel: () => void;
  onError: (message: string | null) => void;
}

/** One position: its cultivar and trees, or moved to another row and position. */
function PositionEditor({ areaId, position, plantedLabel, onSaved, onCancel, onError }: PositionEditorProps) {
  const [row, setRow] = useState<number | ''>(position.row_number);
  const [seq, setSeq] = useState<number | ''>(position.sequence_number);
  const [cultivarId, setCultivarId] = useState<number | null>(position.cultivar_id);
  const [treeCount, setTreeCount] = useState<number | ''>(position.tree_count);
  const [rootstockId, setRootstockId] = useState<number | null>(position.rootstock_cultivar_id);
  const [plantingDate, setPlantingDate] = useState(position.planting_date?.slice(0, 10) ?? '');
  const [notes, setNotes] = useState(position.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const valid = row !== '' && row > 0 && seq !== '' && seq > 0 && cultivarId != null && treeCount !== '' && treeCount > 0;
  const id = `pos-${position.id}`;

  const save = async () => {
    if (!valid || cultivarId == null) return;
    setBusy(true);
    onError(null);
    try {
      const res = await orchardService.updatePosition(position.id, {
        row_number: Number(row),
        sequence_number: Number(seq),
        cultivar_id: cultivarId,
        tree_count: Number(treeCount),
        rootstock_cultivar_id: rootstockId,
        planting_date: plantingDate || null,
        notes: notes.trim() || null,
      });
      if (res.success && res.data) onSaved(res.data);
      else onError('Could not save the position.');
    } catch (e) {
      onError(errorText(e, 'Could not save the position.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    onError(null);
    try {
      const res = await orchardService.deletePositions(areaId, [position.id]);
      if (res.success && res.data) onSaved(res.data);
      else onError('Could not take the position out.');
    } catch (e) {
      onError(errorText(e, 'Could not take the position out.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 border border-primary-200 bg-primary-50/40 rounded-lg p-4 space-y-3">
      <p className="text-sm font-semibold text-neutral-800 break-words">
        Row {position.row_number} · S{position.sequence_number}
        {plantedLabel && <span className="font-normal text-neutral-500"> · planted {plantedLabel}</span>}
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <label htmlFor={`${id}-row`} className={labelClass}>Row</label>
          <input id={`${id}-row`} type="number" min="1" value={row}
            onChange={(e) => setRow(e.target.value === '' ? '' : parseInt(e.target.value, 10))} className={inputClass} />
        </div>
        <div>
          <label htmlFor={`${id}-seq`} className={labelClass}>Position</label>
          <input id={`${id}-seq`} type="number" min="1" value={seq}
            onChange={(e) => setSeq(e.target.value === '' ? '' : parseInt(e.target.value, 10))} className={inputClass} />
        </div>
        <div className="col-span-2">
          <label htmlFor={`${id}-trees`} className={labelClass}>Trees</label>
          <input id={`${id}-trees`} type="number" min="1" value={treeCount}
            onChange={(e) => setTreeCount(e.target.value === '' ? '' : parseInt(e.target.value, 10))} className={inputClass} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <CultivarPicker
          label="Cultivar"
          cultivarRole="scion"
          value={cultivarId}
          initialDisplayName={nameOf(position.cultivar_name, position.cultivar_trade_name)}
          onChange={(c) => setCultivarId(c?.id ?? null)}
        />
        <CultivarPicker
          label="Rootstock (optional)"
          placeholder="Search rootstock cultivars..."
          cultivarRole="rootstock"
          value={rootstockId}
          initialDisplayName={position.rootstock_cultivar_name
            ? nameOf(position.rootstock_cultivar_name, position.rootstock_cultivar_trade_name)
            : ''}
          onChange={(c) => setRootstockId(c?.id ?? null)}
        />
        <div>
          <label htmlFor={`${id}-planted`} className={labelClass}>Planted</label>
          <input id={`${id}-planted`} type="date" value={plantingDate}
            onChange={(e) => setPlantingDate(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label htmlFor={`${id}-notes`} className={labelClass}>Notes</label>
          <input id={`${id}-notes`} value={notes} maxLength={1000}
            onChange={(e) => setNotes(e.target.value)} className={inputClass} />
        </div>
      </div>
      {confirmingRemove ? (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-3 text-sm text-red-800">
          <p className="break-words">
            Take out row {position.row_number} position {position.sequence_number} and its {trees(position.tree_count)}?
          </p>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <Button variant="danger" size="sm" onClick={remove} disabled={busy}>Take out</Button>
            <Button variant="outline" size="sm" onClick={() => setConfirmingRemove(false)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="primary" size="sm" onClick={save} disabled={busy || !valid}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirmingRemove(true)} disabled={busy}>
            <Trash2 className="h-4 w-4" /> Take out
          </Button>
        </div>
      )}
    </div>
  );
}

export default RowPositionPlanner;
